import {LicenseType} from "../services/LicenseTypeService";
import {DateUtils} from "../utils/DateUtils";

export class Organization {

  public readonly organizationId: string;                 // Only filled for existing organizations
  public organizationName: string;
  public organizationUrl: string;
  public organizationLanguageCode: string = "de";
  public adminSalutation: string;
  public adminTitle: string;
  public adminFirstName: string;
  public adminLastName: string;
  public adminPosition: string;
  public adminPhone: string;
  public contactDepartment: string;
  public contactAddressLine1: string;
  public contactAddressLine2: string;
  public contactAddressLine3: string;
  public contactPostalCode: string;
  public contactCity: string;
  public contactCountryCode: string = "CH";
  public contactPhone: string;
  public contactFax: string;
  public contactEmail: string;
  public isSeparateBillingAddress: boolean = false;
  public billingDepartment: string;
  public billingAddressLine1: string;
  public billingAddressLine2: string;
  public billingAddressLine3: string;
  public billingPostalCode: string;
  public billingCity: string;
  public billingCountryCode: string = "CH";
  public billingPhone: string;
  public billingFax: string;
  public billingEmail: string;
  public readonly currentLicenseCategory: string;         // Only filled if there is a current license
  public readonly currentLicenseInitialLogins: number;    // Only filled if there is a current license
  public readonly currentLicenseRemainingLogins: number;  // Only filled if there is a current license
  public readonly currentLicenseEndDate: string;          // Only filled if there is a current license
  public readonly newLicenseOrderDate: string;            // Only filled if there is a new order already
  public newLicenseTypeName: string;
  public newLicenseTypeDescription: string;
  public newLicenseCategory: string;
  public newLicenseInitialLogins: number;
  public newLicenseIsPlanningEnabled: boolean;
  public newLicenseIsAdminEnabled: boolean;
  public newLicensePriceNet: number;
  public newLicenseTaxPercent: number;
  public newLicenseTax: number;
  public newLicensePriceTotal: number;
  public newLicenseBeginDate: string;
  public newLicenseRemarks: string;
  public newLicenseSignature: string;
  public agbAccepted: boolean = false;

  public get isExistingOrganization(): boolean {
    return !!this.organizationId;
  }

  // The UI uses a "same billing address" instead of a "separate
  // billing address" checkbox, hence the virtual property below.

  public get isSameBillingAddress(): boolean {
    return !this.isSeparateBillingAddress;
  }

  public set isSameBillingAddress(isSameBillingAddress: boolean) {
    this.isSeparateBillingAddress = !isSameBillingAddress;
  }

  public initBillingAddressIfNecessary(): void {
    if (!this.billingAddressLine1) {
      this.copyContactAddressToBillingAddress();
    }
  }

  public copyContactAddressToBillingAddress(): void {
    this.billingDepartment = this.contactDepartment;
    this.billingAddressLine1 = this.contactAddressLine1;
    this.billingAddressLine2 = this.contactAddressLine2;
    this.billingAddressLine3 = this.contactAddressLine3;
    this.billingPostalCode = this.contactPostalCode;
    this.billingCity = this.contactCity;
    this.billingCountryCode = this.contactCountryCode;
    this.billingPhone = this.contactPhone;
    this.billingFax = this.contactFax;
    this.billingEmail = this.contactEmail;
  }

  public get currentLicenseTypeName(): string {
    return (this.currentLicenseCategory === "Agentur" ? this.currentLicenseInitialLogins + " Zugriffe" : this.currentLicenseCategory);
  }

  public get isCurrentLicenseActive(): boolean {
    const today = DateUtils.currentDate();
    return (this.currentLicenseRemainingLogins > 0 && this.currentLicenseEndDate >= today);
  }

  public setNewLicenseType(newLicenseType: LicenseType): void {
    if (newLicenseType) {
      // Copy the entire license type attributes so that
      // if the license type definition is changed later on,
      // the license request will still contain the exact
      // conditions shown to the user.
      this.newLicenseTypeName = newLicenseType.name;
      this.newLicenseTypeDescription = newLicenseType.description;
      this.newLicenseCategory = newLicenseType.category;
      this.newLicenseInitialLogins = newLicenseType.initialLogins;
      this.newLicenseIsPlanningEnabled = newLicenseType.isPlanningEnabled;
      this.newLicenseIsAdminEnabled = newLicenseType.isAdminEnabled;
      this.newLicensePriceNet = newLicenseType.priceNet;
      this.newLicenseTaxPercent = newLicenseType.taxPercent;
      this.newLicenseTax = newLicenseType.tax;
      this.newLicensePriceTotal = newLicenseType.priceTotal;
      this.newLicenseSignature = newLicenseType.signature;
    }
  }

  public selectInitialNewLicenseType(availableLicenseTypes: LicenseType[]): void {
    const currentLicenseType = availableLicenseTypes.find(licenseType => licenseType.category === this.currentLicenseCategory && licenseType.initialLogins === this.currentLicenseInitialLogins);
    this.setNewLicenseType(currentLicenseType || availableLicenseTypes[0]);
  }

  public get earliestNewLicenseBeginDate(): string {
    const today = DateUtils.currentDate();
    return (this.currentLicenseEndDate > today ? this.currentLicenseEndDate : today);
  }

  public get formattedNewLicenseBeginDate(): string {
    if (this.newLicenseBeginDate) {
      return DateUtils.formatAsLocalDate(this.newLicenseBeginDate);
    }
    if (this.isCurrentLicenseActive) {
      return "im Anschluss an aktuelle Lizenz";
    }
    return "sofort";
  }

}
